/**
 * Phase 23-C -- Home dashboard tile that reflects the user's untriaged
 * Inbox count. Clicking jumps to /inbox. Auto-refreshes when a new
 * capture is pushed via the `inbox-item-captured` window event.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function InboxTile() {
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/inbox/count");
      setCount(data.untriaged || 0);
    } catch { /* silent -- widget-level failure isn't user-visible */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    refresh();
    const h = () => refresh();
    window.addEventListener("inbox-item-captured", h);
    return () => window.removeEventListener("inbox-item-captured", h);
  }, [refresh]);

  // Hide the tile entirely on first paint until we know the count -- we
  // don't want to flash a "0 items" state on cold load.
  if (!loaded) return null;
  if (count === 0) return null;

  return (
    <Link
      to="/inbox"
      className="group flex items-center gap-3 rounded-xl border border-sky-500/20 bg-sky-500/[0.06] hover:bg-sky-500/[0.10] px-4 py-3 mb-4 backdrop-blur-md transition-colors"
      data-testid="home-inbox-tile"
    >
      <div className="h-10 w-10 rounded-xl bg-sky-500/15 flex items-center justify-center">
        <Package className="h-5 w-5 text-sky-300" strokeWidth={1.75} />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-[11px] uppercase tracking-widest text-sky-300/80">
          Inbox
        </span>
        <span className="text-sm">
          <strong>{count}</strong> untriaged {count === 1 ? "item" : "items"} waiting
        </span>
      </div>
      <ArrowRight className="ml-auto h-4 w-4 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}
